
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const InvestmentGuide = () => {
  const downloadGuide = () => {
    const guideContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Contract Funding Group Investment Guide</title>
    <style>
        body { 
            font-family: Arial, sans-serif; 
            line-height: 1.6; 
            margin: 40px; 
            color: #333; 
        }
        .header { 
            text-align: center; 
            border-bottom: 3px solid #1f2937; 
            padding-bottom: 20px; 
            margin-bottom: 30px; 
        }
        .section { 
            margin-bottom: 30px; 
        }
        .highlight { 
            background-color: #fef3c7; 
            padding: 15px; 
            border-left: 5px solid #f59e0b; 
            margin: 20px 0; 
        }
        .comparison-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 20px 0; 
        }
        .comparison-table th, .comparison-table td { 
            border: 1px solid #ddd; 
            padding: 12px; 
            text-align: left; 
        }
        .comparison-table th { 
            background-color: #f8f9fa; 
        }
        .risk-levels { 
            display: grid; 
            grid-template-columns: repeat(3, 1fr); 
            gap: 15px; 
            margin: 20px 0; 
        }
        .risk-box { 
            padding: 15px; 
            border: 1px solid #ddd; 
            border-radius: 5px; 
        }
        .low-risk { background-color: #d1fae5; }
        .medium-risk { background-color: #fef3c7; }
        .high-risk { background-color: #fee2e2; }
        .footer { 
            margin-top: 50px; 
            padding-top: 20px; 
            border-top: 1px solid #ddd; 
            text-align: center; 
            font-size: 14px; 
            color: #666; 
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Contract Funding Group Investment Guide</h1>
        <h2>Land Contract Investment Opportunities</h2>
        <p><strong>Comprehensive Guide for Qualified Investors</strong></p>
    </div>

    <div class="section">
        <h2>Executive Summary</h2>
        <p>Contract Funding Group specializes in acquiring and selling land contracts and private mortgages, offering investors unique opportunities to participate in real estate-backed securities with attractive yields and lower volatility than traditional markets.</p>
        
        <div class="highlight">
            <strong>Key Benefits:</strong>
            <ul>
                <li>8-15% annual yields</li>
                <li>Real estate-backed security</li>
                <li>Monthly cash flow</li>
                <li>Lower market volatility</li>
                <li>Minimum investment: $15,000</li>
            </ul>
        </div>
    </div>

    <div class="section">
        <h2>What Are Land Contracts?</h2>
        <p>Land contracts, also known as contracts for deed, are seller-financed real estate agreements where the seller acts as the lender. The buyer makes monthly payments directly to the seller until the contract is paid in full, at which point the deed transfers to the buyer.</p>
        
        <h3>How They Work:</h3>
        <ol>
            <li><strong>Property Sale:</strong> Seller finances the buyer's purchase</li>
            <li><strong>Monthly Payments:</strong> Buyer makes regular payments including principal and interest</li>
            <li><strong>Deed Transfer:</strong> Upon full payment, deed transfers to buyer</li>
            <li><strong>Investment Opportunity:</strong> Sellers can sell their contract to investors for immediate cash</li>
        </ol>
    </div>

    <div class="section">
        <h2>Investment Comparison</h2>
        <table class="comparison-table">
            <thead>
                <tr>
                    <th>Investment Type</th>
                    <th>Expected Return</th>
                    <th>Risk Level</th>
                    <th>Liquidity</th>
                    <th>Minimum Investment</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td><strong>Land Contracts</strong></td>
                    <td><strong>8-15%</strong></td>
                    <td><strong>Medium</strong></td>
                    <td><strong>Low-Medium</strong></td>
                    <td><strong>$15,000</strong></td>
                </tr>
                <tr>
                    <td>Stock Market (S&P 500)</td>
                    <td>6-10%</td>
                    <td>High</td>
                    <td>High</td>
                    <td>$1</td>
                </tr>
                <tr>
                    <td>Corporate Bonds</td>
                    <td>3-6%</td>
                    <td>Medium</td>
                    <td>Medium</td>
                    <td>$1,000</td>
                </tr>
                <tr>
                    <td>Rental Real Estate</td>
                    <td>4-8%</td>
                    <td>Medium</td>
                    <td>Low</td>
                    <td>$50,000+</td>
                </tr>
                <tr>
                    <td>Savings Accounts</td>
                    <td>1-3%</td>
                    <td>Very Low</td>
                    <td>High</td>
                    <td>$1</td>
                </tr>
            </tbody>
        </table>
    </div>

    <div class="section">
        <h2>Risk Assessment</h2>
        <div class="risk-levels">
            <div class="risk-box low-risk">
                <h4>Low Risk Contracts</h4>
                <ul>
                    <li>LTV below 60%</li>
                    <li>Strong payment history</li>
                    <li>Prime locations</li>
                    <li>Established buyers</li>
                </ul>
            </div>
            <div class="risk-box medium-risk">
                <h4>Medium Risk Contracts</h4>
                <ul>
                    <li>LTV 60-80%</li>
                    <li>Good payment history</li>
                    <li>Standard locations</li>
                    <li>Verified income buyers</li>
                </ul>
            </div>
            <div class="risk-box high-risk">
                <h4>Higher Risk Contracts</h4>
                <ul>
                    <li>LTV above 80%</li>
                    <li>Limited payment history</li>
                    <li>Rural/developing areas</li>
                    <li>Higher yield potential</li>
                </ul>
            </div>
        </div>
    </div>

    <div class="section">
        <h2>Due Diligence Process</h2>
        <h3>Our Comprehensive Review Includes:</h3>
        <ul>
            <li><strong>Property Appraisal:</strong> Professional valuation to confirm property value</li>
            <li><strong>Title Research:</strong> Clear title verification and lien searches</li>
            <li><strong>Payment History:</strong> Analysis of buyer's payment track record</li>
            <li><strong>Insurance Verification:</strong> Confirmation of property insurance coverage</li>
            <li><strong>Legal Review:</strong> Contract terms and legal compliance verification</li>
            <li><strong>Market Analysis:</strong> Local real estate market conditions</li>
        </ul>
    </div>

    <div class="section">
        <h2>Investment Process</h2>
        <ol>
            <li><strong>Registration:</strong> Complete investor qualification process</li>
            <li><strong>Review Opportunities:</strong> Access exclusive investment portal</li>
            <li><strong>Due Diligence:</strong> Review detailed investment packages</li>
            <li><strong>Submit Bid:</strong> Competitive bidding on selected opportunities</li>
            <li><strong>Contract Execution:</strong> Finalize purchase and transfer documentation</li>
            <li><strong>Monthly Payments:</strong> Receive monthly principal and interest payments</li>
        </ol>
    </div>

    <div class="section">
        <h2>Tax Considerations</h2>
        <p><strong>Important:</strong> Consult with your tax advisor regarding the tax implications of land contract investments. Generally:</p>
        <ul>
            <li>Interest income is taxable as ordinary income</li>
            <li>Principal payments may have capital gains implications</li>
            <li>Depreciation may be available in certain circumstances</li>
            <li>1031 exchanges may be possible for qualifying properties</li>
        </ul>
    </div>

    <div class="section">
        <h2>Contact Information</h2>
        <p><strong>Contract Funding Group Investment Team</strong></p>
        <p>For additional information or to schedule a consultation:</p>
        <ul>
            <li>Email: investors@contractfundinggroup.com</li>
            <li>Phone: (555) 123-4567</li>
            <li>Website: www.contractfundinggroup.com</li>
        </ul>
    </div>

    <div class="footer">
        <p><strong>Disclaimer:</strong> This guide is for informational purposes only and does not constitute investment advice. All investments carry risk, including potential loss of principal. Past performance does not guarantee future results. Please consult with qualified financial and legal advisors before making any investment decisions.</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
    </div>
</body>
</html>`;

    const blob = new Blob([guideContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Contract-Funding-Group-Investment-Guide.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button variant="outline" onClick={downloadGuide}>
      <Download className="h-4 w-4 mr-2" />
      Download Investment Guide
    </Button>
  );
};

export default InvestmentGuide;
